import { resourcesApi } from '../services/resources-api';

export type OnSourceSelectCallback = (docId: string, source: string) => void;

// Local types for Q&A
interface QASource {
  id: string;
  title: string;
  score: number;
  excerpt: string;
}

interface QAResponse {
  question: string;
  answer: string;
  sources: QASource[];
}

export class KnowledgeQA {
  private panelEl: HTMLElement;
  private inputEl: HTMLInputElement;
  private contentEl: HTMLElement;
  private isOpen: boolean = false;
  private isLoading: boolean = false;
  private currentResponse: QAResponse | null = null;
  private onSourceSelect: OnSourceSelectCallback | null = null;

  constructor() {
    this.createPanel();
    this.panelEl = document.getElementById('knowledge-qa-panel')!;
    this.inputEl = document.getElementById('knowledge-qa-input') as HTMLInputElement;
    this.contentEl = document.getElementById('knowledge-qa-content')!;
    this.setupEventListeners();
  }

  private createPanel(): void {
    const panel = document.createElement('div');
    panel.id = 'knowledge-qa-panel';
    panel.className = 'knowledge-qa-panel hidden';
    panel.innerHTML = `
      <div class="knowledge-qa-container">
        <div class="knowledge-qa-header">
          <h3>Ask Your Knowledge</h3>
          <button id="knowledge-qa-close-btn" class="knowledge-qa-close" title="Close">
            &times;
          </button>
        </div>
        <div class="knowledge-qa-input-row">
          <input
            type="text"
            id="knowledge-qa-input"
            placeholder="Ask a question about your notes..."
            autocomplete="off"
          />
          <button id="knowledge-qa-ask-btn" class="knowledge-qa-ask-btn">
            Ask
          </button>
        </div>
        <div id="knowledge-qa-content" class="knowledge-qa-content">
          <div class="knowledge-qa-hint">
            Ask any question and get answers based on your personal knowledge base.
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(panel);
  }

  private setupEventListeners(): void {
    // Close button
    document.getElementById('knowledge-qa-close-btn')?.addEventListener('click', () => {
      this.close();
    });

    // Ask button
    document.getElementById('knowledge-qa-ask-btn')?.addEventListener('click', () => {
      this.askQuestion();
    });

    // Enter key to submit
    this.inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.askQuestion();
      }
      if (e.key === 'Escape') {
        this.close();
      }
    });

    // Click outside to close
    this.panelEl.addEventListener('click', (e) => {
      if (e.target === this.panelEl) {
        this.close();
      }
    });

    // Keyboard shortcut: Escape to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  setOnSourceSelect(callback: OnSourceSelectCallback): void {
    this.onSourceSelect = callback;
  }

  open(initialQuery?: string): void {
    this.isOpen = true;
    this.panelEl.classList.remove('hidden');

    if (initialQuery) {
      this.inputEl.value = initialQuery;
    }

    this.inputEl.focus();
  }

  close(): void {
    this.isOpen = false;
    this.panelEl.classList.add('hidden');
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  private async askQuestion(): Promise<void> {
    const question = this.inputEl.value.trim();

    if (!question || question.length < 3) {
      return;
    }

    if (this.isLoading) return;

    this.isLoading = true;
    this.renderLoading(question);

    try {
      const response = await resourcesApi.ask(question, 5);

      if (response.success && response.data) {
        this.currentResponse = response.data;
        this.renderResponse();
      } else {
        this.renderError(response.error || 'Failed to get answer');
      }
    } catch (error: any) {
      this.renderError(error.message || 'Network error');
    } finally {
      this.isLoading = false;
    }
  }

  private renderLoading(question: string): void {
    this.contentEl.innerHTML = `
      <div class="knowledge-qa-question">
        <strong>Q:</strong> ${this.escapeHtml(question)}
      </div>
      <div class="knowledge-qa-loading">
        <div class="knowledge-qa-spinner"></div>
        <p>Searching your knowledge base and generating answer...</p>
      </div>
    `;
  }

  private renderError(message: string): void {
    this.contentEl.innerHTML = `
      <div class="knowledge-qa-error">
        <p>Error: ${this.escapeHtml(message)}</p>
        <button class="knowledge-qa-retry-btn">Try Again</button>
      </div>
    `;

    this.contentEl.querySelector('.knowledge-qa-retry-btn')?.addEventListener('click', () => {
      this.askQuestion();
    });
  }

  private renderResponse(): void {
    if (!this.currentResponse) return;

    const { question, answer, sources } = this.currentResponse;

    const sourcesHtml = sources.length > 0
      ? `
        <div class="knowledge-qa-sources">
          <h4>Sources</h4>
          ${sources.map((source, idx) => this.renderSource(source, idx)).join('')}
        </div>
      `
      : '';

    this.contentEl.innerHTML = `
      <div class="knowledge-qa-question">
        <strong>Q:</strong> ${this.escapeHtml(question)}
      </div>
      <div class="knowledge-qa-answer">
        <strong>A:</strong>
        <div class="knowledge-qa-answer-text">${this.formatAnswer(answer)}</div>
      </div>
      ${sourcesHtml}
    `;

    // Add click handlers to sources
    this.contentEl.querySelectorAll('.knowledge-qa-source').forEach(el => {
      el.addEventListener('click', () => {
        const docId = el.getAttribute('data-doc-id');
        const source = el.getAttribute('data-source');

        if (docId && source && this.onSourceSelect) {
          this.onSourceSelect(docId, source);
          this.close();
        }
      });
    });
  }

  private renderSource(source: QASource, index: number): string {
    const scorePercent = Math.round(source.score * 100);

    return `
      <div class="knowledge-qa-source" data-doc-id="${source.id}" data-source="resource">
        <div class="knowledge-qa-source-header">
          <span class="knowledge-qa-source-num">${index + 1}</span>
          <span class="knowledge-qa-source-badge">&#128196;</span>
          <span class="knowledge-qa-source-title">${this.escapeHtml(source.title)}</span>
          <span class="knowledge-qa-source-score">${scorePercent}%</span>
        </div>
        <div class="knowledge-qa-source-excerpt">
          ${this.escapeHtml(source.excerpt)}
        </div>
      </div>
    `;
  }

  private formatAnswer(answer: string): string {
    // Simple markdown-like formatting
    return this.escapeHtml(answer)
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>');
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
